import React, { useState, useEffect } from 'react';
import _, { find, isEmpty, map } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import CloseIcon from '@material-ui/icons/Close';
import { FormControl } from '@material-ui/core';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import 'react-phone-number-input/style.css';
import styles from '../../../EditDevice/editDevice.styles.js';
import moment from 'moment';
import { FormControlLabel, MenuItem } from '@mui/material';
import ReactGoogleAutocomplete from 'react-google-autocomplete';
import { getTimeZoneOffsetInteger } from '../../../utils/index.js';
import Button from '../../../components/Button';
import TextInput from '../../../components/TextInput';
import Switch from '../../../components/Switch';
import SelectInput from '../../../components/SelectInput.jsx';
import Select from '@mui/material/Select';

const useStyles = makeStyles(styles);

const EVENT_TYPE = [
  {
    title: 'Social visit',
    key: 'social_visit',
    color: '#BF76F6',
  },
  {
    title: 'Appointment',
    key: 'appointment',
    color: '#FFC007',
  },
  {
    title: 'Task',
    key: 'task',
    color: '#5CB85B',
  },
];

const WEEK_DAYS = [
  { name: 'Sun' },
  { name: 'Mon' },
  { name: 'Tue' },
  { name: 'Wed' },
  { name: 'Thur' },
  { name: 'Fri' },
  { name: 'Sat' },
];

function AddEvents({ user, closeModal, refreshEvents, selectedRecipient, caregivers }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [loader, setLoader] = useState(false);
  const [state, setState] = useState({
    title: '',
    description: '',
    type: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    assignee: '',
    repeat: false,
    errorMessage: '',
    weekDayFrequency: [],
    remindMe: '',
    location: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const {
        title,
        description,
        type,
        startDate,
        endDate,
        startTime,
        endTime,
        assignee,
        repeat,
        weekDayFrequency,
        remindMe,
        location,
      } = state;
      if (isEmpty(title)) {
        setState((prevState) => {
          return { ...prevState, errorMessage: 'Title cannot be empty!' };
        });
        return;
      }

      if (isEmpty(type)) {
        setState((prevState) => {
          return { ...prevState, errorMessage: 'Event type must be selected!' };
        });
        return;
      }

      if (isEmpty(startDate)) {
        setState((prevState) => {
          return { ...prevState, errorMessage: 'Start date cannot be empty!' };
        });
        return;
      }

      if (isEmpty(endDate)) {
        setState((prevState) => {
          return { ...prevState, errorMessage: 'End date cannot be empty!' };
        });
        return;
      }

      if (isEmpty(startTime)) {
        setState((prevState) => {
          return { ...prevState, errorMessage: 'Start time cannot be empty!' };
        });
        return;
      }

      if (isEmpty(endTime)) {
        setState((prevState) => {
          return { ...prevState, errorMessage: 'End time cannot be empty!' };
        });
        return;
      }

      if (moment(startDate).unix() > moment(endDate).unix()) {
        setState((prevState) => {
          return { ...prevState, errorMessage: 'Start date cannot be passed from end date!' };
        });
        return;
      }

      const [startHour, startMin] = state.startTime.split(':');
      const [endHour, endMin] = state.endTime.split(':');

      if (parseInt(startHour) > parseInt(endHour)) {
        setState((prevState) => {
          return { ...prevState, errorMessage: 'Start time should be before end time!' };
        });
        return;
      }

      if (parseInt(startHour) === parseInt(endHour)) {
        if (parseInt(startMin) > parseInt(endMin)) {
          setState((prevState) => {
            return { ...prevState, errorMessage: 'Start time should be before end time!' };
          });
          return;
        }
      }

      if (repeat && isEmpty(weekDayFrequency)) {
        setState((prevState) => {
          return { ...prevState, errorMessage: 'Must select one or more day!' };
        });
        return;
      }

      setState((prevState) => {
        return { ...prevState, errorMessage: '' };
      });

      const body = {
        title,
        description,
        type,
        color: find(EVENT_TYPE, (item) => item?.key === type)?.color || '',
        startDate,
        endDate,
        startTime,
        endTime,
        assignee,
        repeat,
        weekDayFrequency,
        remindMe,
        location,
        timezone: getTimeZoneOffsetInteger(),
      };
      const userId = user?.id || user?._id;
      setLoader(true);
      if (isEmpty(selectedRecipient)) {
        await axios.post(process.env.REACT_APP_EXPRESS_URL + `/api/canCare/user/${userId}/event`, body, {
          headers: {
            'Content-Type': 'application/json',
            authorization: localStorage.getItem('auth'),
          },
        });
      } else {
        await axios.post(
          process.env.REACT_APP_EXPRESS_URL + `/api/canCare/caregiver/${userId}/userEvents/${selectedRecipient}`,
          body,
          {
            headers: {
              'Content-Type': 'application/json',
              authorization: localStorage.getItem('auth'),
            },
          },
        );
      }

      refreshEvents();
      closeModal();
    } catch (error) {
      console.log('error =====> ', error);
      setState((prevState) => {
        return {
          ...prevState,
          errorMessage: error?.response?.data?.message || 'Something went wrong!',
        };
      });
    } finally {
      setLoader(false);
    }
  };

  const handleSetWeekDays = (name) => {
    if (state.weekDayFrequency.includes(name)) {
      setState((prev) => {
        return {
          ...prev,
          weekDayFrequency: prev.weekDayFrequency.filter((item) => item !== name),
        };
      });
    } else {
      setState((prev) => {
        return {
          ...prev,
          weekDayFrequency: [...prev.weekDayFrequency, name],
        };
      });
    }
  };

  const handleSetAddress = (address) => {
    if (address?.formatted_address) {
      setState((prev) => {
        return {
          ...prev,
          location: address?.formatted_address,
        };
      });
    }
  };

  return (
    <div className={classes.paper}>
      <Grid container item direction="row" justify="space-between" alignItems="center" className={classes.header}>
        <Grid item className={classes.title}>
          Create Task
        </Grid>
        <Grid item className={classes.closeIcon} onClick={closeModal}>
          <CloseIcon />
        </Grid>
      </Grid>

      <Grid item className={classes.userInfoPanel} style={{ marginTop: '20px' }}>
        <div className={classes.newCaregiver}>
          <Grid item className={classes.deviceInfoPart}>
            <TextInput
              label="Task Name"
              fullWidth
              value={state.title}
              onChange={(e) =>
                setState((prevState) => {
                  return { ...prevState, title: e.target.value };
                })
              }
            />
          </Grid>
        </div>
      </Grid>

      <Grid item className={classes.userInfoPanel}>
        <div className={classes.newCaregiver}>
          <Grid item className={classes.deviceInfoPart}>
            <TextInput
              label="Describe task"
              fullWidth
              value={state.description}
              onChange={(e) =>
                setState((prevState) => {
                  return { ...prevState, description: e.target.value };
                })
              }
              multiline
            />
          </Grid>
        </div>
      </Grid>

      <Grid container style={{ width: '800px' }}>
        <Grid item className={classes.userInfoPanel} lg={3} md={3} sm={12} xs={12}>
          <div className={classes.newCaregiver}>
            <Grid item className={classes.deviceInfoPart}>
              <div className={classes.deviceInfoPartTitle}>
                Type<span style={{ color: 'red' }}>*</span>
              </div>
              <FormControl variant="outlined" style={{ width: '190px', marginTop: '10px' }}>
                <Select
                  defaultValue=""
                  displayEmpty
                  fullWidth
                  onChange={(e) =>
                    setState((prevState) => {
                      return { ...prevState, type: e.target.value };
                    })
                  }
                  style={{ borderRadius: 50 }}
                >
                  <MenuItem value="" disabled>
                    -- select type --
                  </MenuItem>
                  {EVENT_TYPE.map(({ title, key, color }, idx) => (
                    <MenuItem value={key} key={idx}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          style={{
                            backgroundColor: color,
                            height: 20,
                            width: 20,
                            marginRight: 10,
                            borderRadius: '50%',
                          }}
                        />
                        {title}
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </div>
        </Grid>

        <Grid item className={classes.userInfoPanel} lg={3} md={3} sm={12} xs={12}>
          <div className={classes.newCaregiver}>
            <Grid item className={classes.deviceInfoPart}>
              <div className={classes.deviceInfoPartTitle}>Assignee</div>
              <FormControl variant="outlined" style={{ width: '190px', marginTop: '10px' }}>
                <Select
                  native
                  fullWidth
                  value={state.assignee}
                  onChange={(e) =>
                    setState((prevState) => {
                      return { ...prevState, assignee: e.target.value };
                    })
                  }
                  style={{ borderRadius: 50 }}
                >
                  <option value="">-- select assignee --</option>
                  {map(caregivers, ({ userId, name }, idx) => (
                    <option key={idx} value={userId}>
                      {name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </div>
        </Grid>

        <Grid item className={classes.userInfoPanel} lg={3} md={3} sm={12} xs={12}>
          <div className={classes.newCaregiver}>
            <Grid item className={classes.deviceInfoPart}>
              <div className={classes.deviceInfoPartTitle}>Remind me</div>
              <SelectInput
                fullWidth
                options={['5 minutes', '10 minutes', '30 minutes']}
                value={state.remindMe}
                onChange={(e) =>
                  setState((prev) => {
                    return { ...prev, remindMe: e.target.value };
                  })
                }
                style={{ marginTop: '10px' }}
              />
            </Grid>
          </div>
        </Grid>

        <Grid item className={classes.userInfoPanel} lg={3} md={3} sm={12} xs={12}>
          <div className={classes.newCaregiver}>
            <Grid item className={classes.deviceInfoPart}>
              <div className={classes.deviceInfoPartTitle}>Location</div>

              <ReactGoogleAutocomplete
                options={{
                  types: ['geocode'],
                  componentRestrictions: { country: 'us' },
                }}
                className="address-input"
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                onPlaceSelected={handleSetAddress}
                style={{ borderRadius: 50, border: '0.5px solid grey', paddingLeft: '10px' }}
              />
            </Grid>
          </div>
        </Grid>
      </Grid>

      <Grid container style={{ width: '800px' }}>
        <Grid item className={classes.userInfoPanel} lg={3} md={3} sm={12} xs={12}>
          <div className={classes.newCaregiver}>
            <Grid item className={classes.deviceInfoPart}>
              <div className={classes.deviceInfoPartTitle}>
                Start Date<span style={{ color: 'red' }}>*</span>
              </div>
              <FormControl variant="outlined">
                <input
                  style={{ width: '190px', borderRadius: 50 }}
                  type="date"
                  className={classes.deviceInfoInput}
                  onChange={(e) =>
                    setState((prevState) => {
                      return { ...prevState, startDate: e.target.value };
                    })
                  }
                />
              </FormControl>
            </Grid>
          </div>
        </Grid>
        <Grid item className={classes.userInfoPanel} lg={3} md={3} sm={12} xs={12}>
          <div className={classes.newCaregiver}>
            <Grid item className={classes.deviceInfoPart}>
              <div className={classes.deviceInfoPartTitle}>
                End Date<span style={{ color: 'red' }}>*</span>
              </div>
              <FormControl variant="outlined">
                <input
                  style={{ width: '190px', borderRadius: 50 }}
                  type="date"
                  className={classes.deviceInfoInput}
                  onChange={(e) =>
                    setState((prevState) => {
                      return { ...prevState, endDate: e.target.value };
                    })
                  }
                  min={moment(state.startDate).format('YYYY-MM-DD')}
                />
              </FormControl>
            </Grid>
          </div>
        </Grid>
        <Grid item lg={3} md={3} sm={12} xs={12}>
          <div>
            Start Time<span style={{ color: 'red' }}>*</span>
          </div>
          <input
            style={{ width: '190px', borderRadius: 50 }}
            type="time"
            value={state.startTime}
            className={classes.deviceInfoInput}
            onChange={(e) =>
              setState((prevState) => {
                return { ...prevState, startTime: e.target.value };
              })
            }
          />
        </Grid>
        <Grid item lg={3} md={3} sm={12} xs={12}>
          <div>
            End Time<span style={{ color: 'red' }}>*</span>
          </div>
          <input
            style={{ width: '190px', borderRadius: 50 }}
            type="time"
            value={state.endTime}
            className={classes.deviceInfoInput}
            onChange={(e) =>
              setState((prevState) => {
                return { ...prevState, endTime: e.target.value };
              })
            }
          />
        </Grid>
      </Grid>

      <Grid>
        <FormControlLabel
          control={
            <Switch
              onChange={() =>
                setState((prevState) => {
                  return { ...prevState, repeat: !prevState.repeat };
                })
              }
            />
          }
          label="Repeat task"
        />
        {state.repeat && (
          <Grid item style={{ marginBlock: '20px' }}>
            <div>
              {WEEK_DAYS.map(({ name }) => (
                <span
                  className="weekDays"
                  style={{
                    color: state.weekDayFrequency.includes(name) ? '#fff' : 'black',
                    background: state.weekDayFrequency.includes(name) ? '#26786a' : '#fff',
                  }}
                  onClick={() => handleSetWeekDays(name)}
                >
                  {name.charAt(0)}
                </span>
              ))}
            </div>
          </Grid>
        )}
      </Grid>

      <Grid
        item
        direction="column"
        justify="flex-start"
        alignItems="center"
        className={classes.errorMessage}
        style={{ color: 'red', marginTop: '10px' }}
      >
        {state.errorMessage}
      </Grid>
      <div style={{ marginBlock: '20px', borderBottom: '0.5px solid grey' }} />
      <Grid container item direction="row" justify="space-between" alignItems="center" className={classes.footer}>
        <Grid item>
          <Button fullWidth bg="light" onClick={closeModal} text="Cancel" />
        </Grid>
        <Grid item>
          <Button loading={loader} fullWidth onClick={handleSubmit} text="Add" />
        </Grid>
      </Grid>
    </div>
  );
}

export default AddEvents;
